import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { apiGet, apiSend } from "../lib/customFetch";
import styles from "./NotificationCenter.module.css";

const SAFE_LINKS = new Set([
  "/stemple",
  "/clockin?fane=fravaer",
  "/clockin?fane=uge",
]);

interface NotificationItem {
  id: string;
  title: string;
  body: string;
  actionLink: string;
  createdAt: string;
  readAt: string | null;
  mailStatus: "afventer" | "claimet" | "sendt" | "fejlet" | null;
}

interface InboxResponse {
  items: NotificationItem[];
  unreadCount: number;
  nextCursor: string | null;
}

interface MailPreference {
  enabled: boolean;
  addressAvailable: boolean;
  address: string | null;
  addressKind: "kontakt_email" | "login_email";
}

type InboxState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ok"; inbox: InboxResponse; preference: MailPreference };

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function isNotificationItem(value: unknown): value is NotificationItem {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<NotificationItem>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.title === "string" &&
    typeof candidate.body === "string" &&
    typeof candidate.actionLink === "string" &&
    typeof candidate.createdAt === "string" &&
    !Number.isNaN(new Date(candidate.createdAt).getTime()) &&
    (candidate.readAt === null ||
      (typeof candidate.readAt === "string" &&
        !Number.isNaN(new Date(candidate.readAt).getTime()))) &&
    (candidate.mailStatus === null ||
      candidate.mailStatus === "afventer" ||
      candidate.mailStatus === "claimet" ||
      candidate.mailStatus === "sendt" ||
      candidate.mailStatus === "fejlet")
  );
}

function isInboxResponse(value: unknown): value is InboxResponse {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<InboxResponse>;
  return (
    Array.isArray(candidate.items) && candidate.items.every(isNotificationItem) &&
    typeof candidate.unreadCount === "number" &&
    (candidate.nextCursor === null || typeof candidate.nextCursor === "string")
  );
}

function isMailPreference(value: unknown): value is MailPreference {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<MailPreference>;
  return (
    typeof candidate.enabled === "boolean" &&
    typeof candidate.addressAvailable === "boolean" &&
    (candidate.address === null || typeof candidate.address === "string") &&
    (candidate.addressKind === "kontakt_email" ||
      candidate.addressKind === "login_email")
  );
}

export function NotificationCenter({
  enabledFeatureKeys,
  layout = "header",
  onUnreadCountChange,
}: {
  enabledFeatureKeys: readonly string[];
  layout?: "header" | "shell";
  onUnreadCountChange?: (count: number) => void;
}) {
  const [state, setState] = useState<InboxState>({ status: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [preferenceBusy, setPreferenceBusy] = useState(false);
  const [pageBusy, setPageBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(() => {
    setState({ status: "loading" });
    setActionError(null);
    Promise.all([
      apiGet<unknown>("/api/notifications?limit=20"),
      apiGet<unknown>("/api/notifications/preference"),
    ])
      .then(([inbox, preference]) => {
        if (!isInboxResponse(inbox) || !isMailPreference(preference)) {
          setState({ status: "failed" });
          return;
        }
        setState({ status: "ok", inbox, preference });
      })
      .catch(() => setState({ status: "failed" }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = (item: NotificationItem) => {
    if (item.readAt !== null || busyId !== null) return;
    setBusyId(item.id);
    setActionError(null);
    apiSend<unknown>("POST", `/api/notifications/${item.id}/read`)
      .then((payload) => {
        if (!isNotificationItem(payload)) {
          throw new Error("Ugyldigt notifikationssvar");
        }
        const updated = payload;
        setState((current) => {
          if (current.status !== "ok") return current;
          return {
            ...current,
            inbox: {
              ...current.inbox,
              unreadCount: Math.max(0, current.inbox.unreadCount - 1),
              items: current.inbox.items.map((candidate) =>
                candidate.id === updated.id ? updated : candidate,
              ),
            },
          };
        });
      })
      .catch(() => setActionError("Kunne ikke markere notifikationen som læst."))
      .finally(() => setBusyId(null));
  };

  const togglePreference = (enabled: boolean) => {
    if (state.status !== "ok" || preferenceBusy) return;
    setPreferenceBusy(true);
    setActionError(null);
    apiSend<unknown>("PATCH", "/api/notifications/preference", { enabled })
      .then((payload) => {
        if (!isMailPreference(payload)) {
          throw new Error("Ugyldigt præferencesvar");
        }
        const preference = payload;
        setState((current) =>
          current.status === "ok" ? { ...current, preference } : current,
        );
      })
      .catch(() => setActionError("Mailindstillingen kunne ikke gemmes."))
      .finally(() => setPreferenceBusy(false));
  };

  const loadMore = () => {
    if (state.status !== "ok" || state.inbox.nextCursor === null || pageBusy) {
      return;
    }
    setPageBusy(true);
    setActionError(null);
    const cursor = encodeURIComponent(state.inbox.nextCursor);
    apiGet<unknown>(`/api/notifications?limit=20&cursor=${cursor}`)
      .then((payload) => {
        if (!isInboxResponse(payload)) throw new Error("Ugyldigt sidesvar");
        setState((current) => {
          if (current.status !== "ok") return current;
          const byId = new Map(
            [...current.inbox.items, ...payload.items]
              .map((item) => [item.id, item] as const),
          );
          return {
            ...current,
            inbox: {
              ...payload,
              items: [...byId.values()],
            },
          };
        });
      })
      .catch(() => setActionError("Flere notifikationer kunne ikke hentes."))
      .finally(() => setPageBusy(false));
  };

  const unreadCount = state.status === "ok" ? state.inbox.unreadCount : 0;
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [onUnreadCountChange, unreadCount]);
  return (
    <details className={styles.center} data-layout={layout}>
      <summary className={styles.trigger}>
        <span>Notifikationer</span>
        {unreadCount > 0 && (
          <span className={styles.badge} aria-label={`${String(unreadCount)} ulæste`}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </summary>
      <section className={styles.panel} aria-label="Notifikationsindbakke">
        <div className={styles.panelHeader}>
          <div>
            <h2>Notifikationer</h2>
            <p>In-app-indbakken virker også uden mail.</p>
          </div>
          {state.status === "ok" && unreadCount > 0 && (
            <span className={styles.unreadText}>{unreadCount} ulæste</span>
          )}
        </div>

        {state.status === "loading" && (
          <p className={styles.calmState} role="status">Henter notifikationer…</p>
        )}
        {state.status === "failed" && (
          <div className={styles.calmState} role="status">
            <p>Kunne ikke hente notifikationer.</p>
            <button type="button" onClick={load}>Prøv igen</button>
          </div>
        )}
        {state.status === "ok" && (
          <>
            {state.inbox.items.length === 0 ? (
              <p className={styles.calmState}>Du har ingen notifikationer endnu.</p>
            ) : (
              <ul className={styles.list} aria-label="Dine notifikationer">
                {state.inbox.items.map((item) => {
                  const safeLink =
                    SAFE_LINKS.has(item.actionLink) &&
                    enabledFeatureKeys.includes("clockin");
                  return (
                    <li
                      key={item.id}
                      className={item.readAt === null ? styles.unread : styles.read}
                    >
                      <div className={styles.itemHeader}>
                        <strong>{item.title}</strong>
                        <time dateTime={item.createdAt}>
                          {dateFormatter.format(new Date(item.createdAt))}
                        </time>
                      </div>
                      <p>{item.body}</p>
                      <div className={styles.itemActions}>
                        {safeLink ? (
                          <Link href={item.actionLink} onClick={() => markRead(item)}>
                            Åbn
                          </Link>
                        ) : (
                          <span>Handlingen kan ikke åbnes sikkert.</span>
                        )}
                        {item.readAt === null && (
                          <button
                            type="button"
                            disabled={busyId === item.id}
                            onClick={() => markRead(item)}
                          >
                            {busyId === item.id ? "Gemmer…" : "Markér som læst"}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {state.inbox.nextCursor !== null && (
              <button
                type="button"
                className={styles.loadMore}
                disabled={pageBusy}
                onClick={loadMore}
              >
                {pageBusy ? "Henter…" : "Hent flere"}
              </button>
            )}

            <div className={styles.preference}>
              <label>
                <input
                  type="checkbox"
                  checked={state.preference.enabled}
                  disabled={!state.preference.addressAvailable || preferenceBusy}
                  onChange={(event) => togglePreference(event.target.checked)}
                />
                Send også notifikationer på mail
              </label>
              {state.preference.addressAvailable ? (
                <p>
                  Mail sendes til {state.preference.address}. In-app er stadig den
                  autoritative indbakke, hvis mail er forsinket eller fejler.
                </p>
              ) : (
                <p>
                  Mail kan ikke aktiveres, fordi din konto mangler en
                  {state.preference.addressKind === "kontakt_email"
                    ? " kontakt-email"
                    : " login-email"}. In-app virker fortsat.
                </p>
              )}
            </div>
          </>
        )}
        {actionError !== null && <p className={styles.actionError} role="alert">{actionError}</p>}
      </section>
    </details>
  );
}
